import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  Select,
  DatePicker,
  Card,
  Typography,
  Space,
  Skeleton,
  Empty,
  Tag,
  Switch,
  Form,
  Tabs,
} from 'antd';
import {
  DashboardOutlined,
  BarChartOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import {
  fetchAnalyticsData,
  fetchLogs,
  setAnalyticsFilters,
  fetchAllUsers,
  fetchApiVolumeByInterval
} from '../store/actions/analytics';
import SystemHealth from '../components/analytics/SystemHealth';
import ApiUsage from '../components/analytics/ApiUsage';
import TopUsers from '../components/analytics/TopUsers';
import ActivityFeed from '../components/analytics/ActivityFeed';
import ApiRequestVolume from '../components/analytics/ApiRequestVolume';
import SystemTotals from '../components/analytics/SystemTotals';
import TopStorageUser from '../components/analytics/TopStorageUser';
import KPIDashboard from '../components/analytics/KPIDashboard';


const { Option } = Select;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

function AnalyticsDashboard() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('overview');
  
  const {
    summaryData,
    logs,
    filters,
    loadingSummary,
    loadingLogs,
    pagination,
    users,
    loadingUsers,
  } = useSelector((state) => state.analytics);

  useEffect(() => {
    dispatch(fetchAllUsers());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAnalyticsData(filters));
    dispatch(fetchLogs(pagination.currentPage, pagination.perPage, filters));
      // dispatch(fetchApiVolumeByInterval(3600, filters)); // This is now handled in KPIDashboard
  }, [dispatch, filters]);

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters };
    if (value) newFilters[filterType] = value;
    else delete newFilters[filterType];
    dispatch(setAnalyticsFilters(newFilters));
  };

  const handleDateChange = (dates, dateStrings) => {
    handleFilterChange('dates', dateStrings);
  };

  const handleGroupByToggle = (checked) => {
    handleFilterChange('group_by', checked ? 'message' : null);
  };

  return (
    <div style={{ padding: '24px', background: '#f5f6fa', minHeight: '100vh' }}>
      {/* Header with Filters */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          background: '#fff',
          padding: '16px 24px',
          marginBottom: 24,
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Title level={3} style={{ marginBottom: 0 }}>
            Activity & Analytics
          </Title>

          <Row gutter={[12, 12]} align="bottom">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filter by user"
                value={filters.user_id}
                onChange={(value) => handleFilterChange('user_id', value)}
                style={{ width: '100%' }}
                allowClear
                loading={loadingUsers}
                showSearch
                optionFilterProp="children"
              >
                {users.map((user) => (
                  <Option key={user.id} value={user.id}>
                    {user.name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                onChange={handleDateChange}
              />
            </Col>
            <Col>
              <Form.Item label="Group by Message" style={{ marginBottom: 0 }}>
                <Switch onChange={handleGroupByToggle} />
              </Form.Item>
            </Col>
          </Row>
        </Space>
      </div>

      {summaryData.metadata && (
        <Row justify="end" style={{ marginBottom: 16, paddingRight: 8 }}>
          <Text type="secondary">
            Generated at: {new Date(summaryData.metadata.generated_at).toLocaleString()} | Date
            Range: <Tag>{summaryData.metadata.date_range.start}</Tag>-
            <Tag>{summaryData.metadata.date_range.end}</Tag>
          </Text>
        </Row>
      )}

      {/* Tabbed Interface */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="large"
        tabBarStyle={{ background: '#fff', padding: '0 24px', borderRadius: 8, marginBottom: 16 }}
      >
        <TabPane
          tab={
            <span>
              <DashboardOutlined />
              Overview
            </span>
          }
          key="overview"
        >
          {/* Summary Cards */}
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card
                title="System Health"
                bordered={false}
                style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
              >
                {loadingSummary ? (
                  <Skeleton active paragraph={{ rows: 4 }} />
                ) : (
                  <SystemHealth data={summaryData.system_health} />
                )}
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card
                title=""
                bordered={false}
                style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
              >
                {loadingSummary ? (
                  <Skeleton active paragraph={{ rows: 4 }} />
                ) : (
                  <ApiUsage data={summaryData.api_usage} />
                )}
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card
                title="Top Users"
                bordered={false}
                style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
              >
                {loadingSummary ? (
                  <Skeleton active paragraph={{ rows: 4 }} />
                ) : (
                  <TopUsers data={summaryData.top_users} />
                )}
              </Card>
            </Col>
          </Row>

          {/* Additional Summary Cards */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} md={8}>
              <Card
                title="System Totals"
                bordered={false}
                style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
              >
                <SystemTotals data={summaryData.system_totals} loading={loadingSummary} />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                title="Top Storage User"
                bordered={false}
                style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
              >
                <TopStorageUser data={summaryData.top_storage_user} loading={loadingSummary} />
              </Card>
            </Col>
          </Row>

          {/* Activity Feed */}
          <Card
            title="Recent Activity"
            bordered={false}
            style={{
              marginTop: 24,
              borderRadius: 12,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            }}
          >
            {loadingLogs ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : logs?.length > 0 ? (
              <ActivityFeed logs={logs} pagination={pagination} />
            ) : (
              <Empty description="No recent activity found" />
            )}
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <BarChartOutlined />
              KPI Dashboard
            </span>
          }
          key="kpi"
        >
          {loadingLogs ? (
            <Skeleton active paragraph={{ rows: 10 }} />
          ) : logs?.length > 0 ? (
            <KPIDashboard logs={logs} summaryData={summaryData} loading={loadingLogs} />
          ) : (
            <Empty description="No data available for KPI visualization" />
          )}
        </TabPane>

        <TabPane
          tab={
            <span>
              <LineChartOutlined />
              Detailed Charts
            </span>
          }
          key="charts"
        >
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Text type="secondary">Additional detailed charts and analytics coming soon...</Text>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
}

export default AnalyticsDashboard;